import { useRef, useState } from 'react';

import { IcUpload, IcPhotoCamera } from '@/assets/images';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/TailwindComponents';
import { IFile } from '@/models';

interface CameraAndUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => void;
  onCameraUpload: (file: File | undefined) => void;
  allowedFileTypes?: string;
}

export const CameraAndUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  onCameraUpload,
  allowedFileTypes = 'image/*',
}: CameraAndUploadModalProps) => {
  const [mode, setMode] = useState<'camera' | 'upload' | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<IFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraLoading, setCameraLoading] = useState(false);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const fileArr: IFile[] = Array.from(files).map((file) => ({
      name: file.name,
      size: file.size,
      mimeType: file.type,
      file,
    }));
    setSelectedFiles(fileArr);
  };

  const handleCameraUpload = (file: File | undefined) => {
    if (!file) return;
    onCameraUpload(file);
    setSelectedFiles([]);
    setMode(null);
    stopCamera();
    setCameraError(null);
    setHasCamera(false);
    onClose();
  };

  const startCamera = async () => {
    setMode('camera');
    setCameraError(null);
    setCameraLoading(true);

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some(
        (device) => device.kind === 'videoinput',
      );

      if (!hasVideoInput) {
        setCameraError('No camera found on this device.');
        return;
      }

      setHasCamera(true);

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setCameraError('Error accessing camera. Check permissions.');
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `photo-${Date.now()}.png`, {
            type: 'image/png',
          });
          setSelectedFiles([
            {
              name: file.name,
              size: file.size,
              mimeType: file.type,
              file,
            },
          ]);
        }
      }, 'image/png');
    }
    stopCamera();
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  // Reset modal state on close
  const handleClose = () => {
    setSelectedFiles([]);
    setMode(null);
    stopCamera();
    setCameraError(null);
    setHasCamera(false);
    onClose();
  };

  const handleRetake = () => {
    setSelectedFiles([]);
    setMode('camera');
    startCamera();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add photos</DialogTitle>
        </DialogHeader>
        <div className="border-b border-gray-200 my-2" />
        {!mode && (
          <div className="text-center text-lg font-light text-gray-700 mt-2">
            Choose how you’d like to add photos
          </div>
        )}
        {!mode && (
          <div className="flex flex-col gap-4 items-center py-4">
            <div className="flex gap-4 w-full">
              <div
                className="flex-1 flex flex-col items-center justify-center border-2 border-solid rounded-lg p-6 cursor-pointer border-gray-200 hover:border-red-500 transition-colors group bg-white hover:bg-red-50"
                onClick={startCamera}
              >
                <IcPhotoCamera className="h-8 w-8 mb-2 text-red-500 transition-colors" />
                <span className="font-semibold">Use Camera</span>
                <span className="text-gray-400 text-sm text-center">
                  Take a photo using your device&apos;s camera
                </span>
              </div>
              <div
                className="flex-1 flex flex-col items-center justify-center border-2 border-solid rounded-lg p-6 cursor-pointer border-gray-200 hover:border-red-500 transition-colors group bg-white hover:bg-red-50"
                onClick={onUpload}
              >
                <IcUpload className="h-8 w-8 mb-2 text-red-500 transition-colors" />
                <span className="font-semibold">Upload file</span>
                <span className="text-gray-400 text-sm text-center">
                  Select an existing photo from your device
                </span>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept={allowedFileTypes}
                  multiple
                  onChange={handleFileChange}
                />
              </div>
            </div>
          </div>
        )}
        {mode === 'camera' && (
          <div className="flex flex-col items-center py-4">
            {cameraError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {cameraError}
              </div>
            )}
            {hasCamera && !cameraError && (
              <>
                <div style={{ position: 'relative', width: 400, height: 300 }}>
                  {cameraLoading && selectedFiles.length === 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 400,
                        height: 300,
                        background: '#e5e7eb', // Tailwind gray-200
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                      }}
                    >
                      <IcPhotoCamera className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  {selectedFiles.length > 0 && (
                    <div className="mt-4">
                      {selectedFiles[0]?.file && (
                        <img
                          src={URL.createObjectURL(selectedFiles[0].file)}
                          alt="Captured"
                          className="rounded"
                          width={400}
                          height={300}
                        />
                      )}
                    </div>
                  )}

                  {selectedFiles.length === 0 && (
                    <>
                      <video
                        ref={videoRef}
                        width={400}
                        height={300}
                        autoPlay
                        className="rounded mb-4"
                        style={{ background: '#e5e7eb' }}
                        onCanPlay={() => setCameraLoading(false)}
                      />
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={300}
                        className="hidden"
                      />
                    </>
                  )}
                </div>
                {selectedFiles.length === 0 && (
                  <div className="flex gap-2 mt-6">
                    <button
                      className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      onClick={capturePhoto}
                    >
                      Take Photo
                    </button>
                  </div>
                )}
              </>
            )}
            {selectedFiles.length > 0 && (
              <div className="mt-6">
                <div className="flex gap-2 mt-2">
                  <button
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    onClick={() => handleRetake()}
                  >
                    Retake
                  </button>
                  {selectedFiles[0].file && (
                    <button
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      onClick={() => handleCameraUpload(selectedFiles[0].file)}
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
